import {
  Toast,
  ToastAction,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastIcon,
  ToastProvider,
  ToastTitle,
  type ToastTone,
  ToastViewport,
} from '@amore/ui/components/toast'
import {
  BellIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

type ToastActionOptions = {
  label: string
  altText: string
  onClick: () => void
}

export type ToastOptions = {
  title: string
  description?: string
  tone?: ToastTone
  duration?: number
  action?: ToastActionOptions
}

type ToastEntry = ToastOptions & { id: number }
type ToastContextValue = {
  showToast: (options: ToastOptions) => number
  dismissToast: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)
let nextToastId = 0

const toneIcons = {
  neutral: BellIcon,
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  danger: CircleAlertIcon,
} satisfies Record<ToastTone, typeof BellIcon>

const toneIconStyles: Record<ToastTone, string> = {
  neutral: 'text-muted-foreground',
  info: 'text-info-foreground',
  success: 'text-success-foreground',
  warning: 'text-warning-foreground',
  danger: 'text-danger-foreground',
}

export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback((options: ToastOptions) => {
    const id = ++nextToastId
    setToasts((current) => [...current, { ...options, id }].slice(-4))
    return id
  }, [])

  const value = useMemo(() => ({ dismissToast, showToast }), [dismissToast, showToast])

  return (
    <ToastContext value={value}>
      <ToastProvider label="Oznámení" duration={6_000} swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const tone = toast.tone ?? 'neutral'
          const Icon = toneIcons[tone]

          return (
            <Toast
              key={toast.id}
              open
              tone={tone}
              duration={toast.duration}
              onOpenChange={(open) => {
                if (!open) dismissToast(toast.id)
              }}
            >
              <ToastIcon className={toneIconStyles[tone]}>
                <Icon aria-hidden="true" />
              </ToastIcon>
              <ToastContent>
                <ToastTitle>{toast.title}</ToastTitle>
                {toast.description ? (
                  <ToastDescription>{toast.description}</ToastDescription>
                ) : null}
                {toast.action ? (
                  <ToastAction
                    altText={toast.action.altText}
                    onClick={toast.action.onClick}
                    className="-mb-1 -ml-2 mt-2 min-h-8 px-2 py-1"
                  >
                    {toast.action.label}
                  </ToastAction>
                ) : null}
              </ToastContent>
              <ToastClose label="Zavřít oznámení" />
            </Toast>
          )
        })}
        <ToastViewport label="Oznámení ({hotkey})" />
      </ToastProvider>
    </ToastContext>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used within ToasterProvider')
  return context
}
